import AddIcon from '@mui/icons-material/Add'
import { Box, CircularProgress } from '@mui/material'
import Button from '@mui/material/Button'
import Hidden from '@mui/material/Hidden'
import Typography from '@mui/material/Typography'
import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { useGetIdsHealthcareservicesHcsIdPatientsHasInsiConflict } from '@toktokdoctech/tokapi-client'
import { useAddLogMutation } from '../../../api/mutations/__generated__/addLog.gql'
import useCurrentHcs from '../../../hooks/useCurrentHcs'
import useCurrentUser from '../../../hooks/useCurrentUser'
import useUserPermissions from '../../../hooks/useUserPermissions'
import useButtonsStyles from '../../../styles/buttonsStyles'
import PageContent from '../../Common/pageContent/PageContent'
import PageContentBody from '../../Common/pageContent/PageContentBody'
import PageContentHeader from '../../Common/pageContent/PageContentHeader'
import CheckBatchButton from '../../INSi/CheckBatchButton'
import ImportPatientsButton from './ImportPatientsButton'
import PatientDataTable from './PatientDataTable'
import PatientListFilters from './PatientListFilters'
import { Message, PatientFilters, PatientListProps } from './_props'
import FlexBox from '../../Common/flexBox/FlexBox'
import MessageState from '../../MessageState/MessageState'

const PatientList = ({ isWorkbench = false }: PatientListProps) => {
    const { hasUserPermission } = useUserPermissions()
    const buttonsClasses = useButtonsStyles()
    const healthCareService = useCurrentHcs()
    const [filters, setFilters] = useState<PatientFilters>({})
    const [message, setMessage] = useState<Message | null>(null)
    const currentUser = useCurrentUser()
    const { id: hcsId, isSynchroServiceEnabled } = useCurrentHcs()
    const {
        data: insiConflictCheck,
        isLoading: insiConflictCheckLoading,
        error: insiConflictCheckError,
    } = useGetIdsHealthcareservicesHcsIdPatientsHasInsiConflict(hcsId, {
        query: { enabled: !isWorkbench && hasUserPermission('ADMIN') },
    })
    const [sendLog] = useAddLogMutation()

    const handleFiltersChange = (newFilters: PatientFilters) => {
        setMessage(null)
        setFilters(newFilters)
    }

    const handleMessage = (nextMessage: Message | null) => {
        setMessage(nextMessage)
    }

    React.useEffect(() => {
        const fetchData = async () => {
            sendLog({
                variables: {
                    input: {
                        hcsId: healthCareService.id,
                        fromApp: 'IDS',
                        userId: currentUser.id,
                        message: `[PatientList] Accès liste patient`,
                        logLevel: 'INFO',
                    },
                },
            })
        }
        fetchData()
    }, [currentUser.id, healthCareService.id, sendLog])

    const hasInsConflict = insiConflictCheck?.hasInsConflict ?? false

    if (insiConflictCheckError) {
        return <Typography>Une erreur empêche de charger la page</Typography>
    }

    let body
    if (insiConflictCheckLoading)
        body = (
            <FlexBox justifyContent="center" alignItems="center">
                <CircularProgress />
            </FlexBox>
        )
    else if (message)
        body = (
            <Box
                sx={{
                    padding: '3rem 0',
                }}
            >
                <MessageState error={message.level === 'error'} text={message.text} />
            </Box>
        )
    else
        body = (
            <FlexBox
                flexDirection="column"
                gap="3rem"
                sx={{
                    width: '100%',
                }}
            >
                <PatientDataTable filters={filters} onMessage={handleMessage} />
            </FlexBox>
        )

    return (
        <PageContent>
            <PageContentHeader
                left={
                    <PatientListFilters
                        onChange={handleFiltersChange}
                        onMessage={handleMessage}
                        filters={filters}
                        isWorkbench={isWorkbench}
                    />
                }
                right={
                    !isWorkbench &&
                    !isSynchroServiceEnabled && (
                        <>
                            {hasUserPermission('ADMIN') && (
                                <>
                                    <ImportPatientsButton />
                                    <CheckBatchButton displayNotification={hasInsConflict} />
                                </>
                            )}
                            <Button
                                className={buttonsClasses.ButtonBlack}
                                startIcon={<AddIcon />}
                                component={Link}
                                to="/patient"
                                data-cy="dataCy_add_patient"
                            >
                                <Hidden smDown>Ajouter</Hidden>
                            </Button>
                        </>
                    )
                }
            />
            <PageContentBody>{body}</PageContentBody>
        </PageContent>
    )
}

export default PatientList
